import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from "react-hot-toast";
import Spinner from '../../../Component/Spinner/Spinner';
import { HiTrash } from "react-icons/hi";
import useScrollToTop from '../../../hooks/useScrollToTop';
import useTitle from '../../../hooks/useTitle';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';

const ProductsManager = () => {
    const [deletingProduct, setDeletingProduct] = useState(null);

    const closeModal = () => {
        setDeletingProduct(null);
    };

    useScrollToTop();
    useTitle("Products");

    const {
        data: allProducts = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ["allProducts"],
        queryFn: async () => {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/product`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const data = await res.json();

            if (data.success) {
                refetch();
                return data.data;
            } else {
                return [];
            }
        },
    });

    if (isLoading) {
        return <Spinner />;
    }

    const handleDeleteProduct = async (product) => {
        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/products/admin/${product._id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            }
        );
        const data = await res.json();
        if (data.success) {
            toast.success("Product Deleted Successfully");
            refetch();
        } else {
            toast.error(data.error);
        }
    };

    return (
        <div className="px-5">
            <h4 className="mb-4 text-2xl font-semibold text-gray-600 dark:text-gray-300">
                All Products
            </h4>
            <div className="w-full overflow-hidden rounded-lg shadow-xs">
                <div className="w-full overflow-x-auto">
                    <table className="w-full whitespace-no-wrap ">
                        <thead>
                            <tr className="text-xs font-semibold tracking-wide text-left text-gray-700 uppercase border-b dark:border-gray-700 bg-gray-300 dark:text-gray-400 dark:bg-black">
                                <th className="px-4 py-3">Product</th>
                                <th className="px-4 py-3">Resale Price</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Promotion</th>
                                <th className="px-4 py-3">Added</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-900">
                            {allProducts?.map((product) => (
                                <tr className="text-gray-700 dark:text-gray-400" key={product._id}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center text-sm">
                                            {/* <!-- Avatar with inset shadow --> */}
                                            <div className="relative hidden w-8 h-8 mr-3 rounded-full md:block">
                                                <img
                                                    className="object-cover w-full h-full rounded-full"
                                                    src={product?.img}
                                                    alt={product?.title}
                                                    loading="lazy"
                                                />
                                                <div
                                                    className="absolute inset-0 rounded-full shadow-inner"
                                                    aria-hidden="true"
                                                ></div>
                                            </div>
                                            <div>
                                                <p className="font-semibold">{product.product_name}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        $ {product.resellerPrice}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {
                                            product.sold === false ? 
                                            (
                                                    <span className='px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100'>Available</span>
                                            ) :
                                            (
                                                    <span className='px-2 py-1 font-semibold leading-tight text-yellow-700 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100'>Sold Out</span>
                                            )
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {
                                            product.advertisement === true ?
                                            (
                                                    <span className='px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100'>Promoted</span>
                                            ) :
                                            (
                                                    <span className='px-2 py-1 font-semibold leading-tight text-yellow-700 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100'>Not Promoted</span>
                                            )
                                        }
                                    </td>

                                    <td className="px-4 py-3 text-sm">{product.post_time}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center space-x-4 text-sm">
                                            <label
                                                className="flex items-center justify-between text-sm font-medium leading-5 text-blue-700 rounded-lg dark:text-gray-400 dark:hover:text-white hover:text-blue-900 focus:outline-none focus:shadow-outline-gray btn btn-error btn-sm"
                                                aria-label="Delete"
                                                htmlFor="confirmation-modal"
                                                title={
                                                    product?.sold === true
                                                        ? "Product Already Sold"
                                                        : "Delete This Product"
                                                }
                                                disabled={product?.sold === true}
                                                onClick={() => setDeletingProduct(product)}
                                                // onClick={() => handleDeleteProduct(product?._id)}
                                            >
                                                <HiTrash
                                                    className={`w-6 h-6 ${product?.sold === true && "opacity-50"
                                                        }`}
                                                />
                                            </label>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {deletingProduct && (
                    <ConfirmationModal
                        title={`Are you sure you want to delete?`}
                        message={`If you delete ${deletingProduct.title}. It cannot be undone.`}
                        successAction={handleDeleteProduct}
                        successButtonName="Delete"
                        modalData={deletingProduct}
                        closeModal={closeModal}></ConfirmationModal>
                )}
            </div>
        </div>
    );
};

export default ProductsManager;